import { useMemo } from 'react';

import {
  CoreDocumentsState,
  DocumentsSections,
  coreDocumentsDefaultState,
  deleteDocument,
  fetchDocument,
  loadDocuments,
  resetDocuments,
  saveDocument,
} from '@cd3p/core/modules/documents';

import {
  bindActionCreators,
  simplifyBuilder,
  useDispatch,
  useSelector,
} from 'third-party';

import { appSelectors, termsAndConditionsSelectors } from 'selectors';
import { APILanguage } from '@cd3p/core/types/api';

export type TermsAndConditionsState = CoreDocumentsState & {
  loadTermsAndConditionsDocumentsPending: boolean;
  fetchTermsAndConditionsDocumentPending: boolean;
};

export const termsAndConditionsDefaultState: TermsAndConditionsState = {
  loadTermsAndConditionsDocumentsPending: false,
  fetchTermsAndConditionsDocumentPending: false,
  ...coreDocumentsDefaultState,
};

const builder = simplifyBuilder(termsAndConditionsDefaultState, {});

export const loadTermsAndConditionsDocuments = builder.createServerAction(
  loadDocuments('loadTermsAndConditionsDocuments'),
);

export const fetchTermsAndConditionsDocument = builder.createServerAction(
  fetchDocument('fetchTermsAndConditionsDocument'),
);

export const saveTermsAndConditionsDocument = builder.createServerAction(
  saveDocument('saveTermsAndConditionsDocument'),
);

export const deleteTermsAndConditionsDocument = builder.createServerAction(
  deleteDocument('deleteTermsAndConditionsDocument'),
);

export const resetTermsAndConditions = builder.createReduxAction(
  resetDocuments('resetTermsAndConditions'),
);

export const useTermsAndConditions = (
  sectionId = DocumentsSections.TermsAndConditionsWebDispatcher,
  language = APILanguage.English,
) => {
  const dispatch = useDispatch();
  const providerId = useSelector(appSelectors.providerId);

  return useMemo(
    () =>
      bindActionCreators(
        {
          loadTermsAndConditionsDocuments: loadTermsAndConditionsDocuments.bind(
            null,
            providerId,
            sectionId,
            language,
            termsAndConditionsSelectors.termsAndConditions,
          ),
          fetchTermsAndConditionsDocument: fetchTermsAndConditionsDocument.bind(
            null,
            providerId,
            sectionId,
          ),
          saveTermsAndConditionsDocument: saveTermsAndConditionsDocument.bind(
            null,
            providerId,
            sectionId,
            language,
          ),
          deleteTermsAndConditionsDocument:
            deleteTermsAndConditionsDocument.bind(null, providerId, sectionId),
          resetTermsAndConditions: resetTermsAndConditions.bind(null),
        },
        dispatch,
      ),
    [dispatch, providerId, sectionId, language],
  );
};

export const termsAndConditionsReducer = builder.getReducers();
